import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { ThoughtInput } from './components/neural/ThoughtInput.tsx';


const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  height: calc(100vh - 60px); // Account for navbar
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px; // Add gap for spacing
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); // Match quantum theme
`;

const ChatBox = styled.div`
  flex: 1;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow-y: auto;
  padding: 10px;
  margin-bottom: 10px;
  background: #f9f9f9;
  max-height: calc(100% - 60px); // Account for input height
`;

const InputContainer = styled.div`
  display: flex;
  gap: 10px;
  padding: 5px 0;
  background: white;
`;

const Message = styled.div<{ isAi?: boolean }>`
  margin: 5px 0;
  padding: 8px;
  border-radius: 8px;
  max-width: 85%;
  font-size: 14px;
  ${props => props.isAi ? `
    background: #e3f2fd;
    margin-left: 0;
  ` : `
    background: #f0f0f0;
    margin-left: auto;
  `}
`;

const Input = styled.input`
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
`;

const Button = styled.button`
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #ccc;
  }
`;

interface ChatMessage {
  text: string;
  isAi: boolean;
}

// Add these logging functions at the top
const debugLog = (message: string, data?: any) => {
  const timestamp = new Date().toISOString();
  const formattedData = data ? JSON.stringify(data, null, 2) : '';
  console.log(
    `[${timestamp}] %c🤖 ${message}`,
    'color: #00ff88; font-weight: bold;',
    formattedData ? `\n${formattedData}` : ''
  );
};

const debugError = (message: string, error?: any) => {
  const timestamp = new Date().toISOString();
  console.error(
    `[${timestamp}] %c❌ ${message}`,
    'color: #ff4444; font-weight: bold;',
    error
  );
};

export const SimpleChatPage = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatBoxRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  };

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;

    const startTime = Date.now();
    debugLog('Starting chat request', { input: input.trim() });

    // Add user message
    setMessages(prev => [...prev, { text: input, isAi: false }]);
    setInput('');
    setIsLoading(true);

    try {
      debugLog('Sending API request to Ollama');
      const response = await fetch('http://localhost:11434/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: "phi4",
          prompt: input.trim(),
          stream: true
        })
      }).catch(error => {
        debugError('Network error:', {
          message: error.message,
          type: error.name,
          stack: error.stack
        });
        throw new Error('Failed to connect to Ollama. Is it running?');
      });

      debugLog('API response received', { 
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
        timeMs: Date.now() - startTime 
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        debugError('API error response:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`Ollama API error (${response.status}): ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Stream initialization failed');
      }

      debugLog('Stream processing started');
      let accumulatedText = '';
      let chunkCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          debugLog('Stream completed', {
            totalChunks: chunkCount,
            responseLength: accumulatedText.length,
            timeMs: Date.now() - startTime
          });
          break;
        }

        try {
          const chunk = new TextDecoder().decode(value);
          const jsonLines = chunk.split('\n').filter(line => line.trim());
          chunkCount += jsonLines.length;
          
          for (const line of jsonLines) {
            const jsonResponse = JSON.parse(line);
            if (jsonResponse.response) {
              accumulatedText += jsonResponse.response;
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages.find(m => m.isAi);
                if (lastMessage) {
                  lastMessage.text = accumulatedText;
                } else {
                  newMessages.push({ text: accumulatedText, isAi: true });
                }
                return newMessages;
              });
              scrollToBottom();
            }
          }

          // Log progress every 10 chunks
          if (chunkCount % 10 === 0) {
            debugLog('Stream progress', {
              chunks: chunkCount,
              responseLength: accumulatedText.length,
              timeMs: Date.now() - startTime
            });
          }
        } catch (e) {
          debugError('Error processing chunk:', e);
          continue;
        }
      }
    } catch (error) {
      debugError('Chat error:', {
        message: error instanceof Error ? error.message : String(error),
        type: error instanceof Error ? error.name : typeof error,
        stack: error instanceof Error ? error.stack : undefined
      });
      
      let errorMessage = 'Something went wrong. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('Failed to connect')) {
          errorMessage = 'Cannot connect to Ollama. Please make sure it\'s running.';
        } else if (error.message.includes('API error')) {
          errorMessage = error.message;
        }
      }
      
      setMessages(prev => [...prev, { 
        text: errorMessage,
        isAi: true 
      }]);
    } finally {
      setIsLoading(false);
      scrollToBottom();
      debugLog('Chat request completed', {
        totalTimeMs: Date.now() - startTime
      });
    }
  };

  return (
    <Container>
      <ThoughtInput />
      
      <ChatBox ref={chatBoxRef}>
        {messages.map((msg, idx) => (
          <Message key={idx} isAi={msg.isAi}>
            {msg.text}
          </Message>
        ))}
      </ChatBox>
      <InputContainer>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              handleSubmit();
            }
          }}
          placeholder="Type a message..."
          disabled={isLoading}
        />
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Sending...' : 'Send'}
        </Button>
      </InputContainer>
    </Container>
  );
}; 